import { MATCH_MAX_DIST } from "../useCableDestinationInfo";
import type { CablePosition } from "../findCableGaps";
import type { UnroutedDevice } from "../unroutedDevices";
import type { ElementsOffTheRun } from "./types";

const NONE: UnroutedDevice[] = [];
const NEAR = MATCH_MAX_DIST * MATCH_MAX_DIST;

/**
 * The elements on this plan that the cables run never accounted for — neither
 * routed onto a circuit nor published in `devices_without_circuit`.
 *
 * It exists because every surface that could put a device on a circuit read the
 * RUN's roster, and the run's roster is frozen at the moment it executed: *"So
 * I added a new element on my plan. Now I want to assign to it the new circuit
 * id I created. But those elements don't show up."* A symbol he drew afterwards
 * was in the Grundriss-Elemente tab, on the drawing, and in no list that could
 * give it a cable.
 *
 * It is the same hole KARL.md already records for `A6 GLT`, from the other end:
 * a device the run never classified has no `element_id` in the roster, so
 * adoption could not reach it. This publishes the id and the position adoption
 * needs, off the app's own elements.
 *
 * **A device missing from the takeoff is a quantity error, not a cosmetic one**,
 * so this deliberately catches more than the symbols he drew this morning: an
 * element the run simply dropped reads the same way here, and should.
 */
export const elementsOffTheRun: ElementsOffTheRun = ({
  byGroupId,
  baseGeometry,
  unroutedDevices,
}) => {
  if (!baseGeometry) return NONE;

  const knownIds = new Set<string>();
  const knownPoints: CablePosition[] = [];
  for (const state of Object.values(baseGeometry.byCircuit)) {
    for (const path of state.paths) {
      const id = path.elementId?.trim();
      if (id) knownIds.add(id);
      const end = path.waypoints[path.waypoints.length - 1];
      if (end) knownPoints.push(end);
    }
  }
  for (const device of unroutedDevices) {
    if (device.id) knownIds.add(device.id);
    if (device.position) knownPoints.push(device.position);
  }

  // Nothing to compare against is not evidence that everything is missing. A
  // bundle that published no element id and no destination would otherwise
  // report every symbol on the sheet as off the run, which is the false 191 in
  // a new coat — so it refuses rather than guesses.
  if (!knownIds.size && !knownPoints.length) return NONE;

  const off: UnroutedDevice[] = [];
  for (const [groupId, elements] of Object.entries(byGroupId)) {
    for (const element of elements) {
      if (knownIds.has(element.id)) continue;
      const bbox = element.bbox;
      if (!bbox) continue;
      const at = { x: (bbox.x1 + bbox.x2) / 2, y: (bbox.y1 + bbox.y2) / 2 };
      // The SAME positional rule `useCableDestinationInfo` already joins these
      // two rosters by. `element_id` arrived late, so a bundle may carry it on
      // some destinations and not others, and an id test alone would report a
      // device with a cable drawn to it as having none.
      const isPlaced = knownPoints.some((point) => {
        const dx = point.x - at.x;
        const dy = point.y - at.y;
        return dx * dx + dy * dy < NEAR;
      });
      if (isPlaced) continue;
      off.push({
        id: element.id,
        // The group label, which is what the Grundriss-Elemente tab calls it —
        // `summariseUnrouted` matches it back to the same group for the symbol.
        name: groupId,
        system: "",
        reason: "not_in_the_estimate",
        position: at,
      });
    }
  }
  return off.length ? off : NONE;
};
